/**
 * Ad fill (Admin → Traffic, /dashboard/admin/traffic): how many Adsterra slots showed an ad to real readers, per site and
 * unit, over the last 1, 7 or 28 days (worker/src/ad-fill.js, GET /api/dashboard/ads/fill?days=). Each slot whose frame
 * was made counts once: filled, empty (no fill, or a blocker inside the frame) or no report (the frame was blocked, or
 * the reader left first). Counted without identifiers (site/js/ad-fill.js). Days are UTC; "1 day" is today so far.
 */
import { useEffect, useState } from "react";
import { List, ListRow, RowSub, RowTitle } from "../components/shared/List";
import { Cell, Head } from "./Organizations";

export interface FillRow { site: string; unit?: string; slotType?: string; loaded: number; filled: number; empty: number; noreport: number; fillRate: number | null }
export interface FillReport { days: number; since: string; units: FillRow[]; types: FillRow[]; sites: FillRow[]; error?: string }

export const FILL_WINDOWS = [1, 7, 28] as const;
const SITE_NAMES: Record<string, string> = { chicago: "Chicago", nyc: "New York", austin: "Austin", boston: "Boston", seattle: "Seattle", denver: "Denver", philadelphia: "Philadelphia", pennsylvania: "Pennsylvania" };
const count = (n: number) => n.toLocaleString("en-US");
/** A fill rate for the table: "62.5%", or "–" with nothing loaded. */
export const fillPct = (r: number | null) => (r == null ? "–" : `${r}%`);
export const siteLabel = (slug: string) => SITE_NAMES[slug] ?? slug;

function FillTable({ label, rows, first }: { label: string; rows: FillRow[]; first: (r: FillRow) => string }) {
  return (
    <List as="div" className="members admin-analysis-errors" role="table" aria-label={label}>
      <ListRow as="div" className="members__head ls-head" role="row">
        <Head min={180}>{label}</Head><Head width={80}>Loaded</Head><Head width={80}>Filled</Head><Head width={80}>Empty</Head><Head width={90}>No report</Head><Head width={70}>Fill</Head>
      </ListRow>
      {rows.map((r) => (
        <ListRow key={`${r.site}|${first(r)}`} as="div" className="members__row" role="row">
          <Cell min={180} className="approvals__org"><RowTitle as="b">{first(r)}</RowTitle><RowSub>{siteLabel(r.site)}</RowSub></Cell>
          <Cell width={80} className="members__active">{count(r.loaded)}</Cell>
          <Cell width={80} className="members__active">{count(r.filled)}</Cell>
          <Cell width={80} className="members__active">{count(r.empty)}</Cell>
          <Cell width={90} className="members__active">{count(r.noreport)}</Cell>
          <Cell width={70} className="members__active"><b>{fillPct(r.fillRate)}</b></Cell>
        </ListRow>
      ))}
    </List>
  );
}

export function AdFillCard() {
  const [days, setDays] = useState<number>(7);
  const [r, setR] = useState<FillReport | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    fetch(`/api/dashboard/ads/fill?days=${days}`, { credentials: "include" }).then((x) => (x.ok ? x.json() : Promise.reject())).then(setR).catch(() => setFailed(true));
  }, [days]);
  return (
    <section className="approvals-recent" aria-labelledby="ad-fill">
      <h2 className="section-title" id="ad-fill">Ad fill</h2>
      <p className="muted art-small">Adsterra slots whose ad frame loaded, by site and unit: filled (an ad showed), empty (no ad, or a blocker), no report (the frame was blocked or the reader left first). Counted without identifiers; days are UTC.</p>
      <div className="seg traffic-sites" role="radiogroup" aria-label="Ad fill period">
        {FILL_WINDOWS.map((d) => <button key={d} role="radio" aria-checked={days === d} onClick={() => setDays(d)}>{d === 1 ? "Today" : `${d} days`}</button>)}
      </div>
      {failed ? <p className="empty-panel">Couldn't load ad fill. Try again.</p> : !r ? <p className="empty-panel">Loading…</p> : !r.units.length ? <p className="empty-panel">No ad slots counted yet</p> : <>
        <div className="stat-row traffic-kpis">
          {r.sites.map((s) => <div key={s.site} className="stat"><span>{siteLabel(s.site)}</span><b>{fillPct(s.fillRate)}</b><span className="muted art-small">{count(s.filled)} of {count(s.loaded)} filled</span></div>)}
        </div>
        <FillTable label="Unit" rows={r.units} first={(x) => x.unit ?? ""} />
        <h3 className="art-card__title">By page type</h3>
        <FillTable label="Page type" rows={r.types} first={(x) => x.slotType ?? ""} />
      </>}
    </section>
  );
}
